import { z } from "zod";
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const ROLES = ["admin", "moderator", "user"] as const;
type AppRole = (typeof ROLES)[number];

async function assertCallerIsAdmin(callerUserId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", callerUserId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

export interface TeamMember {
  user_id: string;
  email: string;
  role: AppRole;
  granted_at: string;
}

export const listTeamMembers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ members: TeamMember[] }> => {
    await assertCallerIsAdmin(context.userId);

    const { data: roles, error: rolesErr } = await supabaseAdmin
      .from("user_roles")
      .select("user_id, role, created_at")
      .order("created_at", { ascending: false });
    if (rolesErr) throw new Error(rolesErr.message);

    // Look up each user's email via admin API
    const members: TeamMember[] = [];
    for (const r of roles ?? []) {
      const { data: u } = await supabaseAdmin.auth.admin.getUserById(r.user_id);
      members.push({
        user_id: r.user_id,
        email: u?.user?.email ?? "(unknown)",
        role: r.role as AppRole,
        granted_at: r.created_at,
      });
    }
    return { members };
  });

const GrantSchema = z.object({
  email: z.string().trim().email().max(255),
  role: z.enum(ROLES),
});

export const grantRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => GrantSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertCallerIsAdmin(context.userId);

    // Find user by email (admin API). Pagination: scan up to 1000 users.
    const { data: list, error: listErr } = await supabaseAdmin.auth.admin.listUsers({
      page: 1,
      perPage: 1000,
    });
    if (listErr) throw new Error(listErr.message);
    const target = list?.users.find((u) => u.email?.toLowerCase() === data.email.toLowerCase());
    if (!target) {
      throw new Error(
        `No account found for ${data.email}. Ask them to sign up at /admin/login first.`
      );
    }

    const { error: insertErr } = await supabaseAdmin
      .from("user_roles")
      .insert({ user_id: target.id, role: data.role });
    if (insertErr && !insertErr.message.includes("duplicate")) {
      throw new Error(insertErr.message);
    }
    return { success: true, user_id: target.id, email: target.email };
  });

const RevokeSchema = z.object({
  user_id: z.string().uuid(),
  role: z.enum(ROLES),
});

export const revokeRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => RevokeSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertCallerIsAdmin(context.userId);

    // Don't let an admin revoke their own admin role (lockout protection)
    if (data.user_id === context.userId && data.role === "admin") {
      throw new Error("You cannot revoke your own admin role.");
    }

    const { error } = await supabaseAdmin
      .from("user_roles")
      .delete()
      .eq("user_id", data.user_id)
      .eq("role", data.role);
    if (error) throw new Error(error.message);
    return { success: true };
  });
